import { useEffect, useState, type CSSProperties } from "react";
import Button from "@mui/material/Button";
import { useKBar } from "kbar";
import {
  ensureCurrentUser,
  getStoredCurrentUser,
  type CurrentUser,
} from "../Features/Users/userIdentity";

export function UserSettings() {
  const { query } = useKBar();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(() =>
    getStoredCurrentUser(),
  );
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ensureCurrentUser()
      .then(setCurrentUser)
      .catch((requestError) => {
        setError(
          requestError instanceof Error
            ? requestError.message
            : "User request failed",
        );
      });
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <>
      <div style={styles.topRightControls}>
        <button
          type="button"
          style={styles.controlButton}
          onClick={() => query.toggle()}
          aria-label="Open command palette"
          title="Open command palette"
        >
          ⌘
        </button>
        <button
          type="button"
          style={styles.controlButton}
          onClick={() => setOpen(true)}
          aria-label="User settings"
          title="User settings"
        >
          ⚙
        </button>
      </div>

      {open && (
        <div
          style={styles.modalOverlay}
          role="presentation"
          onClick={() => setOpen(false)}
        >
          <section
            style={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-label="User settings"
            onClick={(event) => event.stopPropagation()}
          >
            <header style={styles.header}>
              <h2 style={styles.title}>User Settings</h2>
              <Button type="button" onClick={() => setOpen(false)}>
                Close
              </Button>
            </header>

            {error && <p style={styles.error}>{error}</p>}

            <dl style={styles.details}>
              <div>
                <dt style={styles.label}>Display name</dt>
                <dd style={styles.value}>
                  {currentUser?.displayName ?? "Creating user..."}
                </dd>
              </div>
              <div>
                <dt style={styles.label}>User UUID</dt>
                <dd style={styles.monoValue}>
                  {currentUser?.userId ?? "Creating user..."}
                </dd>
              </div>
            </dl>
          </section>
        </div>
      )}
    </>
  );
}

const styles = {
  topRightControls: {
    position: "fixed",
    top: "12px",
    right: "12px",
    zIndex: 30,
    display: "flex",
    gap: "8px",
  },
  controlButton: {
    width: "36px",
    height: "36px",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    background: "var(--bg)",
    color: "var(--text-h)",
    boxShadow: "var(--shadow)",
    fontSize: "18px",
    lineHeight: 1,
    cursor: "pointer",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 40,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    background: "rgba(0, 0, 0, 0.42)",
    padding: "72px 16px 16px",
    overflowY: "auto",
  },
  panel: {
    width: "min(560px, 100%)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    background: "var(--bg)",
    boxShadow: "var(--shadow)",
    padding: "16px",
    textAlign: "left",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
  },
  title: {
    margin: 0,
    color: "var(--text-h)",
    fontSize: "22px",
    fontWeight: 500,
  },
  error: {
    border: "1px solid #ef4444",
    borderRadius: "8px",
    background: "rgba(239, 68, 68, 0.1)",
    color: "#ef4444",
    padding: "10px 12px",
    marginBottom: "16px",
  },
  details: {
    display: "grid",
    gap: "14px",
    margin: 0,
  },
  label: {
    color: "var(--text)",
    fontSize: "12px",
    textTransform: "uppercase",
  },
  value: {
    margin: "4px 0 0",
    color: "var(--text-h)",
  },
  monoValue: {
    margin: "4px 0 0",
    color: "var(--text-h)",
    fontFamily: "var(--mono)",
    fontSize: "13px",
    overflowWrap: "anywhere",
  },
} satisfies Record<string, CSSProperties>;
